import React, { useContext, useEffect, useRef, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';

import './Layout.scss';

import Logo from 'components/Logo';
import MainMenu from 'components/MainMenu/MainMenu';
import NotificationBell from 'components/NotificationBell';
import NotificationSocket from 'components/NotificationSocket';
import Toasts from 'components/Toasts';
import ThemeSwitcher from 'components/ThemeSwitcher';
import Modals from 'components/Modals';
import Dashboard from 'pages/Dashboard';
import Workers, { WorkerDetail } from 'pages/Workers';
import Rewards from 'pages/Rewards';
import Funds from 'pages/Funds';
import Ledger from 'pages/Ledger';
import Notifications from 'pages/Notifications';
import Repair from 'pages/Repair';
import Settings from 'pages/Settings';
import Login from 'pages/Login';
import SignUp from 'components/SignUp';
import AccountVerification from 'components/AccountVerification';
import PasswordReset from 'components/PasswordReset';
import ProtectedRoute from 'components/ProtectedRoute';
import DownloadRewards from 'components/DownloadRewards';
import DownloadPayouts from 'components/DownloadPayouts';

const Layout = () => {
  const { modal, theme } = useContext(UiContext);
  const { user } = useContext(AuthContext)!;
  const location = useLocation();
  const modalOpen = null !== modal;
  const transitionRef = useRef(null);
  // Mobile slide-in drawer. Hidden entirely on desktop via CSS.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Close the drawer whenever the route changes so it never lingers over a new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const pages = {
    dashboard: {
      HeaderComponent: null,
      title: 'Dashboard',
    },
    workers: {
      HeaderComponent: null,
      title: 'Workers',
    },
    rewards: {
      HeaderComponent: <DownloadRewards />,
      title: 'Rewards',
    },
    funds: {
      HeaderComponent: <DownloadPayouts />,
      title: 'Funds',
    },
    ledger: {
      HeaderComponent: null,
      title: 'Ledger',
    },
    notifications: {
      HeaderComponent: null,
      title: 'Notifications',
    },
    repair: {
      HeaderComponent: null,
      title: 'Repair',
    },
    settings: {
      HeaderComponent: <ThemeSwitcher />,
      title: 'Settings',
    },
  };

  return (
    <div className={classNames({
      container: true,
      [theme as string]: true,
      login: !user,
      'menu-open': menuOpen,
    })}>
      {user && user.date_verified && <NotificationSocket />}
      {user && user.date_verified && (
        <>
          <div className='mobile-topbar'>
            <button
              type='button'
              className={classNames({ 'menu-toggle': true, open: menuOpen })}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span /><span /><span />
            </button>
            <Logo />
            <NotificationBell />
          </div>
          <div
            className={classNames({ 'menu-overlay': true, open: menuOpen })}
            onClick={closeMenu}
          />
          <MainMenu
            className={classNames({ modalOpen, open: menuOpen })}
            onNavigate={closeMenu}
          />
        </>
      )}
      <SwitchTransition>
        <CSSTransition
          key={location.pathname}
          classNames='page-transition'
          nodeRef={transitionRef}
          timeout={0}
        >
          <main ref={transitionRef} className={classNames({ modalOpen })} >
            {user && user.date_verified && (
              <div className='title-box'>
                <h1>{ pages[location.pathname.slice(1).split('/')[0] as keyof typeof pages]?.title ?? '' }</h1>
                {pages[location.pathname.slice(1).split('/')[0] as keyof typeof pages]?.HeaderComponent ?? null }
              </div>
            )}
            <Routes>
              <Route path='' element={<ProtectedRoute><Navigate replace to='/dashboard' /></ProtectedRoute>} />
              <Route path='dashboard' element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path='workers' element={<ProtectedRoute><Workers /></ProtectedRoute>} />
              <Route path='workers/:id' element={<ProtectedRoute><WorkerDetail /></ProtectedRoute>} />
              <Route path='rewards' element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
              <Route path='funds' element={<ProtectedRoute><Funds /></ProtectedRoute>} />
              <Route path='ledger' element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
              <Route path='notifications' element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path='repair' element={<ProtectedRoute><Repair /></ProtectedRoute>} />
              <Route path='settings' element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path='login' element={<Login />} />
              <Route path='sign-in' element={<SignUp />} />
              <Route path='reset-password/:token' element={<PasswordReset />} />
              <Route path='reset-password/' element={<PasswordReset />} />
              <Route path='verify-account/:key' element={<AccountVerification />} />
              <Route path='verification-required' element={<AccountVerification />} />
            </Routes>
          </main>
        </CSSTransition>
      </SwitchTransition>
      <Toasts />
      <Modals />
    </div>
  );
};

export default Layout;
